import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@livekatsomo/web/ui-components/react-hook-form-components';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { z } from 'zod';

/**
 * The schema for the Wallsio form, which includes a required `wallsioCode` field.
 */
const formSchema = z.object({
  wallsioCode: z.string().nonempty(),
});

/**
 * Props for the WallsioForm component.
 */
export interface WallsioFormProps {
  /**
   * The Walls.io code to be displayed.
   */
  wallsioCode?: string | null;
  /**
   * Function to be called when the form is submitted.
   * @param values - The form values containing the Walls.io code.
   */
  onSubmit: (values: { wallsioCode: string }) => Promise<void>;
  /**
   * Function to be called when the dialog is closed.
   */
  onClose: () => void;
}

/**
 * WallsioForm component for displaying and submitting a form for WallsioCode.
 * @param wallsioCode - The WallsioCode to be displayed in the form.
 * @param onSubmit - The function to be called when the form is submitted.
 * @param onClose - The function to be called when the form is closed.
 * @returns A form component with WallsioCode input field and submit/cancel buttons.
 */
export function WallsioForm({
  wallsioCode,
  onSubmit,
  onClose,
}: WallsioFormProps) {
  const { t } = useTranslation();
  const initialValues = {
    wallsioCode: wallsioCode || '',
  };

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isSubmitting, isValid },
    reset,
    handleSubmit,
  } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} onReset={() => reset()}>
        <DialogContent>
          <TextField name="wallsioCode" label={t('Code')} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button type="reset" disabled={isSubmitting || !isDirty}>
            {t('Reset')}
          </Button>
          <LoadingButton
            type="submit"
            disabled={!isValid || isSubmitting || !isDirty}
            loading={isSubmitting}
          >
            {t('Save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

export default WallsioForm;
